import { Spinner } from "@heroui/react";
import { type AdminStatsStory, ENDING_RARITY_META } from "@storyline/shared";
import PageHeader from "@/src/common/components/page-header";
import Pill from "@/src/common/components/pill";
import { STORY_STATUS_LABELS, STORY_STATUS_TONES } from "@/src/common/constants/studio-labels";
import StatTile from "@/src/dashboard/components/stat-tile";
import { useAdminStats } from "@/src/dashboard/hooks/use-admin-stats";

const completionRate = (story: AdminStatsStory): string => {
	if (story.playthroughsCount === 0) {
		return "—";
	}
	return `${Math.round((story.completedCount / story.playthroughsCount) * 100)}%`;
};

const DashboardPage: React.FC = () => {
	const { stats, isLoading, hasError } = useAdminStats();

	if (isLoading) {
		return (
			<div className="flex justify-center py-16">
				<Spinner size="lg" />
			</div>
		);
	}

	if (hasError || !stats) {
		return (
			<p className="rounded-xl border border-border-soft bg-surface p-10 text-center text-sm text-danger">
				Impossible de charger les statistiques.
			</p>
		);
	}

	return (
		<div className="flex flex-col gap-6">
			<PageHeader title="Statistiques" description="Vue d'ensemble de l'engagement et des lectures." />

			<div className="grid grid-cols-2 gap-4 lg:grid-cols-6">
				<StatTile label="Utilisateurs" value={String(stats.usersCount)} />
				<StatTile label="Séries" value={String(stats.seriesCount)} />
				<StatTile label="Épisodes" value={String(stats.storiesCount)} />
				<StatTile label="Publiés" value={String(stats.publishedStoriesCount)} />
				<StatTile label="Lectures" value={String(stats.playthroughsCount)} />
				<StatTile label="Terminées" value={String(stats.completedPlaythroughsCount)} />
			</div>

			<div className="flex flex-col gap-4">
				<h2 className="text-lg font-semibold text-foreground">Par story</h2>
				{stats.stories.length === 0 ? (
					<p className="rounded-xl border border-border-soft bg-surface p-10 text-center text-sm text-muted">Aucune story à analyser.</p>
				) : (
					stats.stories.map((story) => (
						<section key={story.storyId} className="flex flex-col gap-4 rounded-xl border border-border-soft bg-surface p-5">
							<div className="flex flex-wrap items-center justify-between gap-3">
								<div className="flex items-center gap-3">
									<h3 className="font-semibold text-foreground">{story.title}</h3>
									<Pill tone={STORY_STATUS_TONES[story.status]}>{STORY_STATUS_LABELS[story.status]}</Pill>
								</div>
								<div className="flex flex-wrap gap-4 text-sm text-muted">
									<span>{story.likesCount} likes</span>
									<span>{story.commentsCount} commentaires</span>
									<span>{story.bookmarksCount} favoris</span>
									<span>
										{story.completedCount}/{story.playthroughsCount} lectures ({completionRate(story)})
									</span>
								</div>
							</div>
							{story.endings.length > 0 ? (
								<ul className="flex flex-col gap-2 border-border-soft border-t pt-3">
									{story.endings.map((ending) => (
										<li key={ending.endingId} className="flex items-center justify-between text-sm">
											<span className="text-foreground">
												{ending.name}
												<span className="ml-2 text-xs text-muted">{ENDING_RARITY_META[ending.rarity].label}</span>
											</span>
											<span className="text-muted">{ending.unlockedCount} débloquée(s)</span>
										</li>
									))}
								</ul>
							) : null}
						</section>
					))
				)}
			</div>
		</div>
	);
};

export default DashboardPage;
